import type { PersonaProfile } from '../shared/types';

/**
 * Persona assignment (§5.3.1, §14.2).
 *
 * In summative the persona is selected at random from the scenario's
 * eligiblePersonas, seeded and stored on the attempt — and the server re-derives
 * it from that seed rather than trusting the client's copy (§9.7, §14.7). Which
 * is why the derivation lives here, beside the scorer, and not in the SPA.
 *
 * Persona randomisation is the strongest anti-memorisation measure in the
 * system: the same scenario plays materially differently across seven personas,
 * so a memorised path fails.
 */

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Derive the persona for an attempt from its seed. Deterministic, so a stored
 * attempt can be replayed exactly as it was sat (§1.1).
 *
 * `exclude` carries the rep's most recent persona, which §14.2 excludes so the
 * same one is not served twice running — unless it is the only one eligible.
 */
export function derivePersona(
  seed: number,
  eligiblePersonas: string[],
  exclude?: string | null
): string {
  if (eligiblePersonas.length === 0) throw new Error('scenario has no eligible personas');
  const pool = eligiblePersonas.filter((p) => p !== exclude);
  const from = pool.length > 0 ? pool : eligiblePersonas;
  const index = Math.floor(mulberry32(seed)() * from.length);
  return from[Math.min(index, from.length - 1)]!;
}

export const personaById = (personas: PersonaProfile[], id: string | null): PersonaProfile | null =>
  personas.find((p) => p.id === id) ?? null;
